/**
 * The MULTI-CHANNEL report template — one document, a section per channel that delivered.
 *
 * The three seeded templates are each single-channel and are selected by precedence, so a
 * display+audio buy (88% of audio SOs) renders as EITHER a display or an audio report and drops
 * the other channel's section. The legacy never did that: `MultiChannelReporting` emits one
 * report with a Display section AND an Audio section, gated on whether each delivered. This
 * template is that shape, expressed declaratively.
 *
 * ── Per-channel bindings ──
 *
 * The composite data model keeps each channel's own figures in `channels[]` and the campaign-wide
 * rollup in `overall`. So the OVERALL summary binds `overall.*`, and each channel SECTION binds
 * its own entry via a JSONata predicate — `channels[channel='DISPLAY'].delivery.clicks` — which is
 * stable whatever order the channels appear in. Sections are gated with `visibleWhen`, the same
 * mechanism the display/dooh/email templates already use for conditional blocks, so a channel with
 * no delivery contributes no pages.
 *
 * Theme and chrome are shared with the Display template rather than restated — one white-label
 * look, one place to change it.
 */

import type { Template, BlockInstanceT } from './template-schema.js';
import { DISPLAY_TEMPLATE } from './display-template.js';

/**
 * The KPI + delivery + breakdown blocks for one channel, bound to `root`.
 *
 * `root` is a data path: `overall` for the rollup, `channels[channel='DISPLAY']` for a section.
 * The block TYPES are the same across every channel — the catalogue is channel-agnostic (§4.4);
 * only the labels and the bound path change.
 */
function deliveryBlocks(root: string, idPrefix: string, ctrFootnote: string): BlockInstanceT[] {
  return [
    {
      id: `${idPrefix}-kpis`,
      type: 'KpiTileRow',
      props: { columns: 4 },
      bindings: {
        tiles: `[
          {"label": "Targeted Impressions",  "value": ${root}.delivery.impressions,         "icon": "eye"},
          {"label": "Total Clicks",          "value": ${root}.delivery.clicks,              "icon": "cursor"},
          {"label": "Retargeted Impressions","value": ${root}.delivery.retargetImpressions, "icon": "audience"},
          {"label": "Retargeted Clicks",     "value": ${root}.delivery.retargetClicks,      "icon": "retarget"}
        ]`,
      },
    },
    {
      id: `${idPrefix}-delivery`,
      type: 'DeliveryStrip',
      props: { ctrFootnote },
      bindings: {
        contracted: `${root}.contract.targetImpressions`,
        overDelivered: `${root}.derived.overDeliveredImpressions`,
        overDeliveryPct: `${root}.derived.overDeliveryPct`,
        ctr: `${root}.derived.ctr`,
      },
    },
    {
      id: `${idPrefix}-breakdown`,
      type: 'Columns',
      props: { columns: 3 },
      bindings: {},
      children: [
        {
          id: `${idPrefix}-device-cat`,
          type: 'DeviceCategoryCard',
          props: {},
          bindings: { mobile: `${root}.delivery.mobileClicks`, desktop: `${root}.delivery.desktopClicks` },
        },
        {
          id: `${idPrefix}-channels`,
          type: 'CampaignChannelsCard',
          props: {},
          bindings: { channels: 'campaign.channels', retargetClicks: `${root}.delivery.retargetClicks` },
        },
        {
          id: `${idPrefix}-browsers`,
          type: 'BrowserTypeCard',
          props: { maxItems: 5, imageLogos: true },
          bindings: { rows: `${root}.browsers` },
        },
      ],
    },
  ];
}

export const COMPOSITE_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-composite',
  name: 'White Label Default — Multi-Channel',
  version: 1,
  status: 'draft',
  agencyId: null,
  // Selection metadata: the channels this template can carry. Not used for rendering — the
  // per-section visibleWhen decides what actually appears.
  channels: ['DISPLAY', 'AUDIO'],
  lifecycle: ['INTERIM', 'POSTFLIGHT'],

  theme: DISPLAY_TEMPLATE.theme,
  chrome: DISPLAY_TEMPLATE.chrome,

  pages: [
    // ---------------------------------------------------- 1: overall summary --
    {
      id: 'overall',
      mode: 'fixed',
      title: 'Campaign summary — all channels',
      blocks: [
        {
          id: 'meta',
          type: 'CampaignMetaCard',
          props: {},
          bindings: {
            reportSo: 'campaign.reportSo',
            client: 'campaign.clientName',
            period: `report.periodStart & " – " & report.periodEnd`,
          },
        },
        {
          id: 'overall-note',
          type: 'RichTextBlock',
          props: {},
          bindings: {
            // The campaign-wide CTR basis, stated. When audio is present it is the streams-swung
            // figure — the same footnote the audio template renders, driven by the data not a
            // hardcoded string that would be false on a mixed report.
            html: `"Campaign totals across all channels. CTR is calculated against " & $lookup({
                     "contracted-impressions": "contracted impressions",
                     "streams": "total streams delivered",
                     "recipients": "recipients delivered"
                   }, overall.derived.ctrBasis) & "."`,
          },
        },
        ...deliveryBlocks('overall', 'overall', ''),
        {
          id: 'overall-device-detail',
          type: 'DeviceDetailsTable',
          props: { maxRows: 8 },
          bindings: { rows: 'overall.devices' },
        },
        {
          id: 'provenance',
          type: 'ProvenanceNote',
          props: {},
          bindings: { show: 'provenance.hasAdjustedFigures' },
          visibleWhen: 'provenance.hasAdjustedFigures',
        },
      ],
    },

    // ---------------------------------------------------- 2: display section --
    {
      id: 'display-section',
      mode: 'fixed',
      title: 'Display',
      // Only when the campaign delivered on display.
      visibleWhen: `$exists(channels[channel='DISPLAY'])`,
      blocks: [
        {
          id: 'display-heading',
          type: 'RichTextBlock',
          props: {},
          bindings: { html: `"<h2>Display</h2>"` },
        },
        ...deliveryBlocks(
          `channels[channel='DISPLAY']`, 'display',
          'CTR is calculated against contracted impressions.',
        ),
        {
          id: 'display-device-detail',
          type: 'DeviceDetailsTable',
          props: { maxRows: 8 },
          bindings: { rows: `channels[channel='DISPLAY'].devices` },
        },
      ],
    },

    // ---------------------------------------------------- 3: audio section --
    {
      id: 'audio-section',
      mode: 'fixed',
      title: 'Streaming audio',
      // The audio side-car exists exactly when the campaign delivered on audio.
      visibleWhen: `$exists(audio)`,
      blocks: [
        {
          id: 'audio-heading',
          type: 'RichTextBlock',
          props: {},
          bindings: { html: `"<h2>Streaming Audio</h2>"` },
        },
        {
          id: 'audio-kpis',
          type: 'KpiTileRow',
          props: { columns: 4 },
          bindings: {
            tiles: `[
              {"label": "Total Streams",        "value": audio.streams,                         "icon": "audio"},
              {"label": "Total Clicks",         "value": channels[channel='AUDIO'].delivery.clicks, "icon": "cursor"},
              {"label": "Comp. Display Impr.",  "value": audio.complimentaryDisplayImpressions,  "icon": "audience"},
              {"label": "Companion Clicks",     "value": audio.companionClicks,                 "icon": "retarget"}
            ]`,
          },
        },
        {
          id: 'audio-listening',
          type: 'MetricTrio',
          props: {},
          bindings: {
            metrics: `[
              {"label": "Ads Played", "value": audio.adsPlayed, "format": "int"},
              {"label": "Reach",      "value": audio.reach,     "format": "pct"},
              {"label": "LTR",        "value": audio.ltr,       "format": "pct"}
            ]`,
          },
        },
        {
          id: 'audio-delivery',
          type: 'DeliveryStrip',
          props: { ctrFootnote: '' },
          bindings: {
            contracted: `channels[channel='AUDIO'].contract.targetImpressions`,
            overDelivered: `channels[channel='AUDIO'].derived.overDeliveredImpressions`,
            overDeliveryPct: `channels[channel='AUDIO'].derived.overDeliveryPct`,
            ctr: `channels[channel='AUDIO'].derived.ctr`,
          },
        },
      ],
    },

    // ---------------------------------------------------- 4: stations --
    {
      id: 'stations',
      mode: 'flowing',
      title: 'Station performance',
      visibleWhen: `$exists(audio) and $count(audio.topStations) > 0`,
      blocks: [
        {
          id: 'stations-card',
          type: 'SectionCard',
          props: { title: 'Top Stations by Plays' },
          bindings: {},
          children: [
            {
              id: 'stations-table',
              type: 'PaginatedTable',
              props: {
                columns: JSON.stringify([
                  { key: 'name', label: 'Station', widthPct: 62 },
                  { key: 'plays', label: 'Plays', widthPct: 20 },
                  { key: 'share', label: 'Share', widthPct: 18 },
                ]),
                zebra: true,
                maxRows: 500,
              },
              bindings: {
                rows: `audio.topStations.{
                  "name": name,
                  "plays": $formatNumber(plays, "#,##0"),
                  "share": $formatNumber(plays / $sum(%.topStations.plays) * 100, "0.0") & "%"
                }`,
                totalRows: '$count(audio.topStations)',
              },
            },
          ],
        },
      ],
    },
  ],

  createdAt: '2026-08-24T00:00:00.000Z',
  createdBy: 'multi-channel-composition',
};
